import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/layout/AppSidebar";
import { AppHeader } from "@/components/layout/AppHeader";
import { MobileHeader } from "@/components/layout/MobileHeader";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

export const AppLayout = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Handle auth errors gracefully
        if (event === "TOKEN_REFRESHED") {
          console.log("Token refreshed successfully");
        }
        
        if (event === "SIGNED_OUT" || !session) {
          navigate("/auth");
        }
      }
    );

    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          // Handle stale/invalid refresh tokens
          if (error.message?.includes("Refresh Token") || error.message?.includes("refresh_token")) {
            console.warn("Session expired, signing out...");
            await supabase.auth.signOut();
            navigate("/auth");
            setLoading(false);
            return;
          }
          throw error;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (!session) {
          navigate("/auth");
          setLoading(false);
          return;
        }

        // Check for existing membership
        const { data: membership, error: membershipError } = await supabase
          .from("memberships")
          .select("organization_id")
          .eq("user_id", session.user.id)
          .limit(1)
          .maybeSingle();

        if (membershipError) {
          console.error("Membership check error:", membershipError);
          // If RLS blocks the query, still allow navigation
        }
        
        if (membership?.organization_id) {
          setOrganizationId(membership.organization_id);
        } else {
          navigate("/onboarding");
        }
        setLoading(false);
      } catch (err) {
        console.error("Auth initialization error:", err);
        navigate("/auth");
        setLoading(false);
      }
    };

    initializeAuth();

    return () => subscription.unsubscribe();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !organizationId) {
    return null;
  }

  // Mobile layout
  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col">
        <MobileHeader user={user} organizationId={organizationId} />
        <main className="flex-1 overflow-auto pb-20">
          <Outlet context={{ user, organizationId }} />
        </main>
        <MobileBottomNav />
      </div>
    );
  }

  // Desktop layout
  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar organizationId={organizationId} />
        <div className="flex-1 flex flex-col">
          <AppHeader user={user} organizationId={organizationId} />
          <main className="flex-1 overflow-auto">
            <Outlet context={{ user, organizationId }} />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};
